import type { MultiPoint, Polygon, Position } from "geojson";

/** Lector de WKB (Well-Known Binary) a GeoJSON, sin dependencias, para las geometrías que manda
 *  Plataforma por gRPC (AOI y lotes = Polygon, conteo = MultiPoint). Copia propia de Plataforma (las
 *  apps no comparten código cliente); espera WKB estándar de OGC 2D sin SRID (`ST_AsBinary`). */

const WKB_POINT = 1;
const WKB_POLYGON = 3;
const WKB_MULTIPOINT = 4;

/** Cursor de lectura: cada geometría (y cada miembro de una Multi*) trae su propio byte de orden. */
class WkbReader {
  private offset = 0;

  /** @param view Vista sobre los bytes del WKB. */
  constructor(private readonly view: DataView) {}

  /** Lee el byte de orden (1 = little-endian). */
  private leerLittleEndian(): boolean {
    const orden = this.view.getUint8(this.offset);
    this.offset += 1;
    return orden === 1;
  }

  /** Lee un entero sin signo de 32 bits. */
  private leerUint32(le: boolean): number {
    const valor = this.view.getUint32(this.offset, le);
    this.offset += 4;
    return valor;
  }

  /** Lee un par de coordenadas (x, y) en float64. */
  private leerPosicion(le: boolean): Position {
    const x = this.view.getFloat64(this.offset, le);
    const y = this.view.getFloat64(this.offset + 8, le);
    this.offset += 16;
    return [x, y];
  }

  /** Lee el header (orden + tipo) y valida que sea el tipo esperado; devuelve el orden de bytes. */
  private leerHeader(tipoEsperado: number): boolean {
    const le = this.leerLittleEndian();
    const tipo = this.leerUint32(le);
    if (tipo !== tipoEsperado) throw new Error(`WKB: se esperaba tipo ${tipoEsperado}, llegó ${tipo}`);
    return le;
  }

  /** Polygon: cantidad de anillos y, por anillo, cantidad de puntos y coordenadas. */
  leerPolygon(): Polygon {
    const le = this.leerHeader(WKB_POLYGON);
    const anillos = this.leerUint32(le);
    const coordinates: Position[][] = [];
    for (let i = 0; i < anillos; i++) {
      const puntos = this.leerUint32(le);
      const anillo: Position[] = [];
      for (let j = 0; j < puntos; j++) anillo.push(this.leerPosicion(le));
      coordinates.push(anillo);
    }
    return { type: "Polygon", coordinates };
  }

  /** MultiPoint: cada miembro es un Point completo con su propio header. */
  leerMultiPoint(): MultiPoint {
    const le = this.leerHeader(WKB_MULTIPOINT);
    const cantidad = this.leerUint32(le);
    const coordinates: Position[] = [];
    for (let i = 0; i < cantidad; i++) {
      const leMiembro = this.leerHeader(WKB_POINT);
      coordinates.push(this.leerPosicion(leMiembro));
    }
    return { type: "MultiPoint", coordinates };
  }
}

/** Vista sobre los bytes (respeta el offset del `Uint8Array`, que puede ser un recorte de otro buffer). */
function vista(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

/** WKB → `Polygon` GeoJSON (AOI de la finca, geometría de un lote). */
export function wkbToPolygon(bytes: Uint8Array): Polygon {
  return new WkbReader(vista(bytes)).leerPolygon();
}

/** WKB → `MultiPoint` GeoJSON (conteo de árboles: un punto por árbol). */
export function wkbToMultiPoint(bytes: Uint8Array): MultiPoint {
  return new WkbReader(vista(bytes)).leerMultiPoint();
}

